import React, { useMemo } from 'react'
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table'
import Skeleton from 'react-loading-skeleton';
import { useDragToScroll } from '../../../hooks/useDragToScroll';

export default function LogsTable({ headers = [], data = [], title = 'All Logs', loading = false, isDark = false, centerAlign = false }) {
  const { scrollContainerRef, topScrollRef, isDragging, scrollWidth, dragHandlers, topScrollHandler } = useDragToScroll([data, loading]);
  const columns = useMemo(() => {
    return headers.map((header) => ({
      ...(header.includes('.')
        ? { accessorFn: (row) => row[header] }
        : { accessorKey: header }
      ),
      header,
      cell: ({ getValue }) => getValue(),
    }))
  }, [headers])

  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() })

  const skeletonBaseColor = isDark ? '#1f1f1f' : '#e9e9e9';
  const skeletonHighlightColor = isDark ? '#2a2a2a' : '#f5f5f5';

  return (
    <div className={`border-solid border-[1px] rounded-[16px] sm:rounded-[16px] overflow-hidden transition-colors duration-200 ${
      isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]'
    }`}>
      <div className={`border-b border-solid border-[1px] h-[56px] sm:h-[68px] flex items-center transition-colors duration-200 ${
        isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]'
      }`}>
        <div className="px-[16px] sm:px-[24px]">
          <h2 className={`m-0 font-bold text-[14px] sm:text-[16px] transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>{title}</h2>
        </div>
      </div>
      <div
        ref={topScrollRef}
        onScroll={topScrollHandler}
        className={`overflow-x-auto ${isDark ? '[&::-webkit-scrollbar-track]:bg-[#000000] [&::-webkit-scrollbar-thumb]:bg-[#292929] [&::-webkit-scrollbar-thumb]:hover:bg-[#4d4d4d] [&::-webkit-scrollbar-button]:bg-[#161515]' : '[&::-webkit-scrollbar-track]:bg-[#f5f5f5] [&::-webkit-scrollbar-thumb]:bg-[#ebebeb] [&::-webkit-scrollbar-thumb]:hover:bg-[#e5e5e5] [&::-webkit-scrollbar-button]:bg-[#d9d9d9]'} [&::-webkit-scrollbar]:h-[10px] [&::-webkit-scrollbar]:w-[16px] [&::-webkit-scrollbar-button]:w-[16px] [&::-webkit-scrollbar-button]:h-[18px]`}
      >
        <div style={{ width: scrollWidth, height: 1 }} />
      </div>
      <div
        ref={scrollContainerRef}
        {...dragHandlers}
        className={`overflow-x-auto select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} [&_button]:cursor-pointer [&_a]:cursor-pointer ${isDark ? '[&::-webkit-scrollbar-track]:bg-[#000000] [&::-webkit-scrollbar-thumb]:bg-[#292929] [&::-webkit-scrollbar-thumb]:hover:bg-[#4d4d4d] [&::-webkit-scrollbar-button]:bg-[#161515]' : '[&::-webkit-scrollbar-track]:bg-[#f5f5f5] [&::-webkit-scrollbar-thumb]:bg-[#ebebeb] [&::-webkit-scrollbar-thumb]:hover:bg-[#e5e5e5] [&::-webkit-scrollbar-button]:bg-[#d9d9d9]'} [&::-webkit-scrollbar]:h-[10px] [&::-webkit-scrollbar]:w-[16px] [&::-webkit-scrollbar-button]:w-[16px] [&::-webkit-scrollbar-button]:h-[18px]`}
      >
        <table className="w-full">
          <thead className={`transition-colors duration-200 ${isDark ? 'bg-[#1c1c1c]' : 'bg-[#072837]'}`}>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} className={`h-[48px] sm:h-[57px] px-[16px] sm:px-[24px] ${centerAlign ? 'text-center' : 'text-left'} font-bold text-[14px] sm:text-[16px] whitespace-nowrap transition-colors duration-200 ${
                    isDark ? 'text-white' : 'text-white'
                  }`}>
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="p-[16px]">
                  <Skeleton
                    count={10}
                    height={57}
                    baseColor={skeletonBaseColor}
                    highlightColor={skeletonHighlightColor}
                  />
                </td>
              </tr>
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row, idx) => {
                // Apply error row color if Is Success is false
                const isErrorRow = row.original["Is Success"] === false;
                const rowBgClass = isDark
                  ? isErrorRow
                    ? 'bg-[#582523]'
                    : idx % 2 === 0
                      ? 'bg-[#161515]'
                      : 'bg-[#1c1c1c]'
                  : isErrorRow
                    ? 'bg-[#f9d6d5]'
                    : idx % 2 === 0
                      ? 'bg-white'
                      : 'bg-gray-50';
                const textColorClass = isDark
                  ? isErrorRow
                    ? 'text-white'
                    : 'text-[#b0b0b0]'
                  : isErrorRow
                    ? 'text-[#072837]'
                    : 'text-[#686868]';
                const borderColor = isDark ? 'border-[#292929]' : 'border-[#d9d9d9]';

                return (
                  <tr key={row.id} className={`border-b border-solid border-[1px] ${borderColor} ${rowBgClass} transition-colors duration-200`}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className={`h-[48px] sm:h-[57px] px-[16px] sm:px-[24px] py-[8px] sm:py-[10px] align-middle whitespace-nowrap font-medium text-[16px] ${centerAlign ? 'text-center' : ''} ${textColorClass} transition-colors duration-200`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className={`px-[16px] sm:px-[24px] py-[16px] sm:py-[24px] text-center font-medium text-[14px] transition-colors duration-200 ${
                  isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'
                }`}>
                  No submission logs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}


